"use client";

import { SHOW_ADVERTISING_PROMPT } from "@repo/ui/lib/advertising-consent-policy";
import { useCallback, useEffect, useRef, useState } from "react";
import { getStoredConsent, isYouthAdsOff } from "@/lib/consent";
import type { ConsentChoice } from "@/lib/consent";
import { commitFreshConsentChoice } from "@/lib/analytics-consent-choice";

// Advertising-preference UI: accept / reject / manage. Ordinary product analytics are always on and are not
// what this asks about (see @/lib/consent). "Accept" is the only thing that loads the Meta / LinkedIn
// advertising tags (@/lib/ad-pixels); "Reject", "Save" with the box off, or no choice at all leaves them off.
//
// REVOCATION: the same panel reopens, toggle set to the stored choice, from the small "Advertising preferences"
// control this component renders at the end of /privacy-policy, on the `sg:advertising-preferences` window event,
// or on arrival at / navigation to `#advertising-preferences`. Saving "off" revokes the tags (see
// commitFreshConsentChoice).
//
// Renders on every host (not gated to LIVE_HOSTS the way Analytics.tsx's gtag calls are): a visitor
// on staging seeing and exercising the control is part of proving it works, and the banner itself
// sets no tracking cookie and calls no analytics endpoint — only `sg_consent_advertising` (see
// @/lib/consent), which is the preference record itself, not a tracking signal.
//
// Shows only when no choice has been recorded yet (`getStoredConsent() === null`). Accept/Reject
// record a choice and close it; Manage expands an inline panel with the one real choice this site
// has to offer (advertising; one category, not GA4's four) and a Save action that records whichever
// state the toggle is in when pressed — including "off", which is how a visitor reaches an explicit
// "denied" without using the Reject shortcut.
export const OPEN_ADVERTISING_PREFERENCES_EVENT = "sg:advertising-preferences";
const PREFERENCES_HASH = "#advertising-preferences";
const POLICY_PATH = "/privacy-policy";

export default function ConsentBanner() {
  // `visible` starts `false` on both the server render and the client's first render, so there is no
  // hydration mismatch for React to discard (a lazy initializer that read the cookie — this file's prior
  // shape — ran during the server render too, where `document` doesn't exist, so it always resolved
  // `true` there regardless of what a returning visitor's browser already held; the client's own first
  // render then disagreed, and React drops the mismatched server subtree, orphaning the buttons before
  // any click handler attaches). The real cookie read is client-only and happens after mount below.
  const [visible, setVisible] = useState(false);
  const [managing, setManaging] = useState(false);
  const [advertisingOn, setAdvertisingOn] = useState(false);
  // Client-only (set after mount), like `visible`, so the server and first client render agree.
  const [onPolicyPage, setOnPolicyPage] = useState(false);

  const openPreferences = useCallback(() => {
    setAdvertisingOn(getStoredConsent() === "granted");
    setManaging(true);
    setVisible(true);
  }, []);

  // Deferred (rather than called synchronously in the effect body) to satisfy
  // react-hooks/set-state-in-effect: `document.cookie` is a synchronous read, but committing its result
  // to state one frame later — instead of during the effect's own commit — is what the rule wants, and
  // costs nothing here since the banner is intentionally allowed one render before it can appear. The
  // rAF is cancelled on unmount so a fast unmount (e.g. route change) never calls setState after teardown.
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      setVisible(SHOW_ADVERTISING_PROMPT && getStoredConsent() === null);
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  // Reopen for a visitor who already chose, so a grant can be withdrawn (or given) later.
  useEffect(() => {
    const onHash = () => {
      if (window.location.hash === PREFERENCES_HASH) openPreferences();
    };
    const raf = requestAnimationFrame(() => {
      setOnPolicyPage(window.location.pathname.replace(/\/+$/, "") === POLICY_PATH);
      onHash();
    });
    window.addEventListener("hashchange", onHash);
    window.addEventListener(OPEN_ADVERTISING_PREFERENCES_EVENT, openPreferences);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("hashchange", onHash);
      window.removeEventListener(OPEN_ADVERTISING_PREFERENCES_EVENT, openPreferences);
    };
  }, [openPreferences]);

  // Record the fresh advertising choice. Product analytics are unaffected by it; initialization is
  // idempotent and never appends a second script tag.
  const decide = (choice: ConsentChoice) => {
    commitFreshConsentChoice(choice);
    setVisible(false);
    setManaging(false);
  };

  // url-nav "Menu -> Contact us" (run 35818367247): this fixed, top-z-index banner covered the design's menu drawer's
  // last row on a 900px-tall viewport, so the click landed here and the visitor never left. While visible, the
  // banner publishes its measured height as --sg-consent-h; the drawer's bottom offset and max-height consume it
  // (design-sources.ts, DC_SHEET_*), so every drawer row stays above the banner. Cleared on close and unmount.
  const bannerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const root = document.documentElement;
    if (!visible) { root.style.removeProperty("--sg-consent-h"); return; }
    const measure = () => root.style.setProperty("--sg-consent-h", `${bannerRef.current?.offsetHeight ?? 0}px`);
    measure();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    if (ro && bannerRef.current) ro.observe(bannerRef.current);
    return () => { ro?.disconnect(); root.style.removeProperty("--sg-consent-h"); };
  }, [visible, managing]);

  if (!visible) {
    // The reachable way back to the choice: one small text control after the privacy policy, in normal flow.
    return onPolicyPage ? (
      <p data-sg-ad-preferences-link="" style={{ margin: 0, padding: "16px 20px", textAlign: "center" }}>
        <button
          type="button"
          onClick={openPreferences}
          style={{ background: "none", border: 0, padding: 0, cursor: "pointer", color: "#2547A5", textDecoration: "underline", font: "inherit", fontSize: 14 }}
        >
          Advertising preferences
        </button>
      </p>
    ) : null;
  }

  return (
    <div
      role="dialog"
      aria-label="Advertising preferences"
      ref={bannerRef}
      data-sg-consent-banner=""
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        // Consent controls must remain clickable above the optional staging member-preview pill.
        zIndex: 2147483001,
      }}
    >
      <style>{CONSENT_CSS}</style>
      <div className="sg-consent-inner">
        <p className="sg-consent-copy">
          We use analytics to understand how the site is used; that is always on. With your permission we
          also use Meta and LinkedIn advertising cookies to measure and show our ads; they stay off unless you accept.
        </p>

        {managing && isYouthAdsOff() ? (
          // Youth 13-15: advertising is not offered at all (the server-set flag); nothing to choose, nothing recorded here.
          <div className="sg-consent-actions" data-sg-consent-youth="">
            <span>Advertising cookies are off for members under 16 and can&apos;t be turned on.</span>
            <button type="button" onClick={() => { setVisible(false); setManaging(false); }} className="sg-consent-btn">
              Close
            </button>
          </div>
        ) : managing ? (
          <div className="sg-consent-actions">
            <label className="sg-consent-check">
              <input
                type="checkbox"
                checked={advertisingOn}
                onChange={(e) => setAdvertisingOn(e.target.checked)}
              />
              Advertising (optional)
            </label>
            <button type="button" onClick={() => decide(advertisingOn ? "granted" : "denied")} className="sg-consent-btn sg-consent-btn--primary">
              Save preferences
            </button>
          </div>
        ) : (
          <div className="sg-consent-actions">
            <button type="button" onClick={() => decide("denied")} className="sg-consent-btn">
              Reject
            </button>
            <button
              type="button"
              onClick={() => {
                setAdvertisingOn(false);
                setManaging(true);
              }}
              className="sg-consent-btn"
            >
              Manage
            </button>
            <button type="button" onClick={() => decide("granted")} className="sg-consent-btn sg-consent-btn--primary">
              Accept
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Kit 2.01 look (public/design/sg-beta-v2.01.html): white surface, glacier border, navy ink, orange primary,
// rounded outline secondaries, DIN Pro caps on controls. The design's colour/font variables are declared on the
// design root and in its bundle, not necessarily on <body> where this banner lives, so every token carries the
// Kit's own literal as its fallback. Scoped to [data-sg-consent-banner]; nothing global.
const CONSENT_CSS = `
[data-sg-consent-banner]{background:#fff;color:#133B55;border-top:1px solid var(--sg-border,#D5E4E8);box-shadow:0 -14px 32px -18px rgba(19,59,85,.28);font-family:var(--sg-font-primary,"Montserrat",-apple-system,"Segoe UI",sans-serif);font-size:13.5px;line-height:1.5;box-sizing:border-box}
[data-sg-consent-banner] *{box-sizing:border-box}
[data-sg-consent-banner] .sg-consent-inner{max-width:1240px;margin:0 auto;display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:12px 24px;padding:14px max(20px,env(safe-area-inset-right)) calc(14px + env(safe-area-inset-bottom,0px)) max(20px,env(safe-area-inset-left))}
[data-sg-consent-banner] .sg-consent-copy{margin:0;flex:1 1 360px;max-width:44rem;color:#133B55}
[data-sg-consent-banner] .sg-consent-actions{display:flex;flex-wrap:wrap;align-items:center;gap:8px}
[data-sg-consent-banner] .sg-consent-check{display:inline-flex;align-items:center;gap:8px;min-height:44px;padding-right:8px;cursor:pointer;font-weight:600}
[data-sg-consent-banner] .sg-consent-check input{width:18px;height:18px;margin:0;accent-color:var(--sg-color-avalanche-orange,#FC500E);cursor:pointer}
[data-sg-consent-banner] .sg-consent-btn{min-width:96px;min-height:44px;padding:0 20px;border-radius:12px;border:1px solid var(--sg-border-strong,#A9C8CF);background:#fff;color:#133B55;cursor:pointer;font-family:var(--sg-font-secondary,"DIN Pro","Roboto",sans-serif);font-size:19px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;white-space:nowrap}
[data-sg-consent-banner] .sg-consent-btn:hover{background:var(--sg-glacier-50,#EEF4F5)}
[data-sg-consent-banner] .sg-consent-btn--primary{border-color:var(--sg-color-avalanche-orange,#FC500E);background:var(--sg-color-avalanche-orange,#FC500E);color:#fff}
[data-sg-consent-banner] .sg-consent-btn--primary:hover{background:var(--sg-orange-500,#E5450A);border-color:var(--sg-orange-500,#E5450A)}
[data-sg-consent-banner] .sg-consent-btn:focus-visible,[data-sg-consent-banner] .sg-consent-check input:focus-visible{outline:2px solid var(--sg-color-alpine-blue,#2547A5);outline-offset:2px}
@media (max-width:640px){
[data-sg-consent-banner]{font-size:13px}
[data-sg-consent-banner] .sg-consent-inner{gap:10px;padding-top:12px}
[data-sg-consent-banner] .sg-consent-actions{width:100%}
[data-sg-consent-banner] .sg-consent-btn{flex:1 1 0;min-width:0;padding:0 10px}
[data-sg-consent-banner] .sg-consent-check{flex:1 1 100%}
}
`;
